import dayjs from 'lib/dayjs';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { payloadAtom } from 'components/Authorization';
import { atomWithDefault } from 'jotai/utils';

export const defaultCursor: Review.Cursor[] = [
  {
    fieldName: 'review_rating',
    direction: 'ASC',
  },
  {
    fieldName: 'has_content',
    direction: 'DESC',
  },
  {
    fieldName: 'comment_time',
    direction: 'DESC',
  },
  {
    fieldName: 'id',
    direction: 'ASC',
  },
];

export const getDefaultFilte = (platform: Lowercase<Review.Platform>): Review.Filte => {
  return {
    platform,
    handleStatus: ['1', '2'],
    ...{
      shopee: {
        reviewRating: ['1', '2', '3'],
      },
      lazada: {
        reviewRating: ['1', '2', '3'],
        productRating: ['1', '2', '3', '4', '5'],
        sellerRating: ['1', '2', '3', '4', '5'],
        logisticsRating: ['1', '2', '3', '4', '5'],
      },
      mercado: {
        reviewRating: ['1', '2', '3'],
      },
      tiktok: {
        reviewRating: ['1', '2', '3'],
      },
      tiktok_us: {
        reviewRating: ['1', '2', '3'],
      },
    }[platform],
    hasContent: 'all',
    reviewStartTime: dayjs().subtract(1, 'days').startOf('days').valueOf().toString(),
    reviewEndTime: dayjs().endOf('days').valueOf().toString(),
  };
};

export const getDefaultSearch = (platform: Lowercase<Review.Platform>): Review.Search => {
  const defaultFilte = getDefaultFilte(platform);

  return {
    ...defaultFilte,
    size: 10,
    cursor: defaultCursor,
  };
};

const cachePrefix = 'fett/reviewSearch';

export const resetCursor = <S extends { cursor: Review.Cursor[] }>(search: S): S => {
  const { cursor: preCursor } = search;

  const cursor = preCursor.map<Review.Cursor>(({ fieldName, direction }) => {
    return {
      fieldName,
      direction,
    };
  });

  return {
    ...search,
    cursor,
  };
};

const getCachedSearch = (platform: Lowercase<Review.Platform>, userId?: number): Review.Search => {
  if (!userId) {
    return getDefaultSearch(platform);
  }

  const cacheKey = `${cachePrefix}-${platform}-${userId}`;

  const cache = localStorage.getItem(cacheKey);

  if (!cache) {
    return getDefaultSearch(platform);
  }

  try {
    const cachedSearch: Review.SearchCache = JSON.parse(cache);

    const { day, productRatingV2, sellerRatingV2, logisticesRatingV2, ...others } =
      resetCursor(cachedSearch);

    const dealV2 = (v2: string[] | undefined) => {
      if (platform !== 'lazada') {
        return undefined;
      }

      if (!v2) {
        return ['1', '2', '3', '4', '5'];
      }

      return v2.filter((_) => _ !== '0');
    };

    const productRating = dealV2(productRatingV2);
    const sellerRating = dealV2(sellerRatingV2);
    const logisticsRating = dealV2(logisticesRatingV2);

    if (!day) {
      return {
        ...others,
        productRating,
        sellerRating,
        logisticsRating,
      };
    }

    return {
      ...others,
      productRating,
      sellerRating,
      logisticsRating,
      reviewStartTime: dayjs()
        .subtract(day - 1, 'days')
        .startOf('days')
        .valueOf()
        .toString(),
      reviewEndTime: dayjs().endOf('days').valueOf().toString(),
    };
  } catch (err) {
    return getDefaultSearch(platform);
  }
};

export const quickDays = [2, 7, 31];

const search2searchCache = (search: Review.Search): Review.SearchCache => {
  const { productRating, logisticsRating, sellerRating } = search;

  return {
    ...search,
    productRatingV2: productRating,
    logisticesRatingV2: logisticsRating,
    sellerRatingV2: sellerRating,
  };
};

const getSearchCache = (search: Review.Search): Review.SearchCache => {
  const searchCache = search2searchCache(search);

  const { reviewStartTime, reviewEndTime } = searchCache;

  if (!reviewStartTime || !reviewEndTime) {
    return searchCache;
  }

  const startTime = parseInt(reviewStartTime, 10);
  const endTime = parseInt(reviewEndTime, 10);

  if (!dayjs(endTime).isSame(dayjs().endOf('days'))) {
    return searchCache;
  }

  for (const day of quickDays) {
    if (
      dayjs()
        .startOf('days')
        .subtract(day - 1, 'days')
        .isSame(dayjs(startTime))
    ) {
      return {
        ...searchCache,
        day,
      };
    }
  }

  return searchCache;
};

const effects = (state: Review.Search, platform: General.Platform, userId: number) => {
  const searchCache = getSearchCache(state);

  const cacheKey = `${cachePrefix}-${platform}-${userId}`;

  localStorage.setItem(cacheKey, JSON.stringify(searchCache));
};

const reviewShopeeSearchAtom = atomWithDefault((get) => {
  const payload = get(payloadAtom);
  return getCachedSearch('shopee', payload?.id);
});
export const reviewShopeeSearchState = atom(
  (get) => {
    return get(reviewShopeeSearchAtom);
  },
  (get, set, state: Review.Search) => {
    set(reviewShopeeSearchAtom, state);
    const payload = get(payloadAtom);
    if (payload?.id) {
      effects(state, 'shopee', payload?.id);
    }
  },
);

const reviewLazadaSearchAtom = atomWithDefault((get) => {
  const payload = get(payloadAtom);
  return getCachedSearch('lazada', payload?.id);
});
export const reviewLazadaSearchState = atom(
  (get) => {
    return get(reviewLazadaSearchAtom);
  },
  (get, set, state: Review.Search) => {
    set(reviewLazadaSearchAtom, state);
    const payload = get(payloadAtom);
    if (payload?.id) {
      effects(state, 'lazada', payload?.id);
    }
  },
);

const reviewMercadoSearchAtom = atomWithDefault((get) => {
  const payload = get(payloadAtom);
  return getCachedSearch('mercado', payload?.id);
});
export const reviewMercadoSearchState = atom(
  (get) => {
    return get(reviewMercadoSearchAtom);
  },
  (get, set, state: Review.Search) => {
    set(reviewMercadoSearchAtom, state);
    const payload = get(payloadAtom);
    if (payload?.id) {
      effects(state, 'mercado', payload?.id);
    }
  },
);

const reviewTikTokSearchAtom = atomWithDefault((get) => {
  const payload = get(payloadAtom);
  return getCachedSearch('tiktok', payload?.id);
});
export const reviewTikTokSearchState = atom(
  (get) => {
    return get(reviewTikTokSearchAtom);
  },
  (get, set, state: Review.Search) => {
    set(reviewTikTokSearchAtom, state);
    const payload = get(payloadAtom);
    if (payload?.id) {
      effects(state, 'tiktok', payload?.id);
    }
  },
);

const reviewTikTokUSSearchAtom = atomWithDefault((get) => {
  const payload = get(payloadAtom);
  return getCachedSearch('tiktok_us', payload?.id);
});
export const reviewTikTokUSSearchState = atom(
  (get) => {
    return get(reviewTikTokUSSearchAtom);
  },
  (get, set, state: Review.Search) => {
    set(reviewTikTokUSSearchAtom, state);
    const payload = get(payloadAtom);
    if (payload?.id) {
      effects(state, 'tiktok_us', payload?.id);
    }
  },
);

export const useShopeeSearch = () => useAtomValue(reviewShopeeSearchState);

export const useSetShopeeSearch = () => useSetAtom(reviewShopeeSearchState);

export const useLazadaSearch = () => useAtomValue(reviewLazadaSearchState);

export const useSetLazadaSearch = () => useSetAtom(reviewLazadaSearchState);

export const useMercadoSearch = () => useAtomValue(reviewMercadoSearchState);

export const useSetMercadoSearch = () => useSetAtom(reviewMercadoSearchState);

export const useTikTokSearch = () => useAtomValue(reviewTikTokSearchState);

export const useSetTikTokSearch = () => useSetAtom(reviewTikTokSearchState);

export const useTikTokUSSearch = () => useAtomValue(reviewTikTokUSSearchState);

export const useSetTikTokUSSearch = () => useSetAtom(reviewTikTokUSSearchState);
